"use client";
import React, { useState, FC, useMemo, useContext, useCallback } from "react";

import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Typography from "@mui/material/Typography";

import { AccessibilityKeys } from "~/constants/accessibility";
import { AccessibilityLabelsContext } from "~/context/accessibility-label/context";
import { replaceCmsVariables } from "~/helpers/replace-cms-variables";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { DropdownArrow } from "~/theme/icons/components/dropdown-arrow";
import { Nullable } from "~/types/general.types";

import { SORTING_DROPDOWN, SORTING_DROPDOWN_CLASSES } from "./constants";
import { SortingDropdownContainer } from "./styled";

export interface SortingDropdownProps {
  items: { value: string; text?: Nullable<string> }[];
  label?: Nullable<string>;
  activeSortingParam?: string;
  setActiveSortingParam?: (value: string) => void;
}

export const SortingDropdown: FC<SortingDropdownProps> = ({
  items,
  label,
  activeSortingParam,
  setActiveSortingParam,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const accessibilityLabels = useContext(AccessibilityLabelsContext);

  const handleOpenState = (open: boolean) => () => setIsOpen(open);

  const handleChange = useCallback(
    (event: SelectChangeEvent) => {
      setActiveSortingParam?.(event.target.value);
    },
    [setActiveSortingParam],
  );

  const sortingText = useMemo(() => {
    return items?.filter((el) => el.value === activeSortingParam)[0].text;
  }, [activeSortingParam, items]);

  const sortByLabel = replaceCmsVariables(
    accessibilityLabels?.[AccessibilityKeys.SortBy],
    {
      sorting: sortingText ?? undefined,
    },
  );

  return (
    <SortingDropdownContainer
      id="sorting-control"
      variant="standard"
      {...getTestAutomationProps("sorting-form-control")}
    >
      <Box
        sx={{
          display: { xs: "none", sm: "none", md: "none", lg: "block", xl: "block" },
        }}
      >
        <InputLabel
          id="sortLabel"
          className={SORTING_DROPDOWN_CLASSES.label}
          onClick={handleOpenState(!isOpen)}
        >
          <Typography variant="body1">{label}&nbsp;</Typography>
        </InputLabel>
      </Box>
      <Select
        variant="standard"
        IconComponent={() => <DropdownArrow />}
        value={activeSortingParam}
        open={isOpen}
        onOpen={handleOpenState(true)}
        onClose={handleOpenState(false)}
        onChange={handleChange}
        displayEmpty
        label={items[0].value}
        MenuProps={{
          PaperProps: {
            sx: {
              width: SORTING_DROPDOWN.paperWidth,
            },
          },
          slotProps: {
            root: {
              slotProps: {
                backdrop: {
                  sx: {
                    background: "transparent",
                  },
                },
              },
            },
          },
        }}
        SelectDisplayProps={{
          "aria-label": sortByLabel ?? undefined,
        }}
      >
        {items?.map((item, index) => (
          <MenuItem key={index} value={item.value}>
            <Typography variant="body1">{item.text}</Typography>
          </MenuItem>
        ))}
      </Select>
    </SortingDropdownContainer>
  );
};
